import { useCallback, useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '../stores/useAuthStore';
import { API_BASE, fetchCsrfCookie, getCsrfToken, api } from '../lib/api';
import { closeOfflineDb } from '../lib/offline/offlineDb';
import { clearPersistedQueryCache } from '../lib/dexieQueryPersister';
import { useSettingsStore, type UserPreferences } from '../stores/useSettingsStore';

export interface BranchOffice {
  name: string;
  address: string;
  website: string | null;
}

export interface AuthTerritory {
  id: number;
  code: string;
  name: string;
}

export interface AuthUser {
  id: number;
  name: string;
  email: string;
  roles: string[];
  is_active: boolean;
  preferences?: UserPreferences;
  branch_office: BranchOffice | null;
  territories?: AuthTerritory[];
}

export function useAuthUser(enabled = true) {
  return useQuery<AuthUser>({
    queryKey: ['auth-user'],
    queryFn: () => api<AuthUser>('/auth/me', { method: 'POST' }),
    enabled,
    staleTime: 5 * 60 * 1000,
    retry: false,
  });
}

export function useAuth() {
  const { isLoggedIn, setLoggedIn } = useAuthStore();
  const queryClient = useQueryClient();
  const { data: user = null } = useAuthUser(isLoggedIn);
  const hydrateFromServer = useSettingsStore((s) => s.hydrateFromServer);

  useEffect(() => {
    if (user?.preferences) {
      hydrateFromServer(user.preferences);
    }
  }, [user?.preferences, hydrateFromServer]);

  const hasRole = useCallback(
    (role: string) => user?.roles.includes(role) ?? false,
    [user],
  );

  const login = useCallback(async (email: string, password: string): Promise<string | null> => {
    try {
      await fetchCsrfCookie();
      const res = await fetch(`${API_BASE}/auth/login`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'X-XSRF-TOKEN': getCsrfToken(),
        },
        body: JSON.stringify({ email, password }),
      });
      const data = await res.json();
      if (!res.ok) return data.message || 'Login failed';
      queryClient.clear();
      await clearPersistedQueryCache();
      setLoggedIn(true);
      return null;
    } catch {
      return 'Connection error';
    }
  }, [setLoggedIn, queryClient]);

  const logout = useCallback(async () => {
    try {
      await fetch(`${API_BASE}/auth/logout`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Accept': 'application/json',
          'X-XSRF-TOKEN': getCsrfToken(),
        },
      });
    } catch {
      // Ignore network errors on logout
    }
    queryClient.clear();
    closeOfflineDb();
    await clearPersistedQueryCache();
    setLoggedIn(false);
  }, [queryClient, setLoggedIn]);

  return { isLoggedIn, user, hasRole, login, logout };
}
